import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";

/* ── Shared select style ── */
const selStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", cursor: "pointer",
};

const FilterSelect = ({ label, value, onChange, disabled, children }) => (
  <div>
    <label style={{ fontSize: 12, fontWeight: 600, color: disabled ? "#A0AEC0" : "#2D3748", marginBottom: 6, display: "block" }}>
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={{ ...selStyle, color: disabled ? "#A0AEC0" : "#1A202C", background: disabled ? "#F7FAFC" : "#fff", cursor: disabled ? "not-allowed" : "pointer" }}
      onFocus={(e) => { if (!disabled) e.target.style.borderColor = "#4CAF50"; }}
      onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }}
    >
      {children}
    </select>
  </div>
);

const DetailCell = ({ label, value }) => (
  <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F4F8" }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: value ? "#1A202C" : "#CBD5E0", fontWeight: value ? 500 : 400 }}>{value || "—"}</div>
  </div>
);

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", borderBottom: "1px solid #F7FAFC" }}>
      <span style={{ width: 28, height: 28, borderRadius: 7, background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
        <i className={`mdi ${icon}`} style={{ fontSize: 13, color: "#2E7D32" }} />
      </span>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
        <div style={{ fontSize: 13, color: "#1A202C", fontWeight: 500, marginTop: 1 }}>{value}</div>
      </div>
    </div>
  );
};

const SectionCard = ({ icon, title, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 16, overflow: "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderBottom: "1px solid #EDF2F7" }}>
      <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>{title}</span>
    </div>
    <div style={{ padding: "4px 16px 8px" }}>{children}</div>
  </div>
);

/* ══════════════════════════════════════════════ */

const SHGProfile = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [ranges, setRanges]       = useState([]);
  const [vfcData, setVfcData]     = useState([]);
  const [shgData, setShgData]     = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc]     = useState("");
  const [selectedShgId, setSelectedShgId] = useState("");
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;

  /* Fetch ranges on mount */
  useEffect(() => {
    const fetchRanges = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/ranges`, config);
        setRanges(res.data || []);
      } catch (e) {
        console.error("Error fetching ranges", e);
      } finally {
        setLoading(false);
      }
    };
    fetchRanges();
  }, [Api]);

  /* Reset selections when global JFMC type changes */
  useEffect(() => {
    setSelectedRange(""); setSelectedVfc(""); setSelectedShgId("");
    setVfcData([]); setShgData([]);
  }, [selectedJfmcType]);

  const handleRangeChange = async (e) => {
    const rangeId = e.target.value;
    setSelectedRange(rangeId);
    setSelectedVfc(""); setSelectedShgId("");
    setVfcData([]); setShgData([]);
    if (!rangeId) return;
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/vfcs/vfcs-by-range`, { rangeId, jfmcTypeId: selectedJfmcType?._id }, config);
      setVfcData(res.data.vfcs || []);
    } catch (e) {
      console.error("Error fetching VFCs", e);
    } finally {
      setLoading(false);
    }
  };

  const handleVfcChange = async (e) => {
    const vfcId = e.target.value;
    setSelectedVfc(vfcId);
    setSelectedShgId(""); setShgData([]);
    if (!vfcId) return;
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/shgs/getShgsByVFC`, { vfcId }, config);
      setShgData(Array.isArray(res.data.shgs) ? res.data.shgs : []);
    } catch (e) {
      console.error("Error fetching SHGs", e);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedRange(""); setSelectedVfc(""); setSelectedShgId("");
    setVfcData([]); setShgData([]);
  };

  const shg = shgData.find((s) => s._id === selectedShgId) || null;

  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>SHG Profile</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>SHG</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Profile View</span>
        </div>
      </div>

      {/* Filter Card */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 24, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
            <i className="mdi mdi-filter-outline" />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>SHG Selection</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Select Range, JFMC and SHG to view the profile</div>
          </div>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 16 }}>
            <FilterSelect label="Range" value={selectedRange} onChange={handleRangeChange}>
              <option value="">Select Range</option>
              {ranges.sort((a, b) => a.rangeName.localeCompare(b.rangeName)).map((r) => (
                <option key={r._id} value={r._id}>{r.rangeName}</option>
              ))}
            </FilterSelect>

            <FilterSelect label="JFMC Name" value={selectedVfc} onChange={handleVfcChange} disabled={!selectedRange}>
              <option value="">Select JFMC</option>
              {vfcData.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((v) => (
                <option key={v._id} value={v._id}>{v.vfcName}</option>
              ))}
            </FilterSelect>

            <FilterSelect label="SHG" value={selectedShgId} onChange={(e) => setSelectedShgId(e.target.value)} disabled={!selectedVfc || shgData.length === 0}>
              <option value="">Select SHG</option>
              {shgData.sort((a, b) => a.name.localeCompare(b.name)).map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </FilterSelect>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handleClear}
              style={{ padding: "8px 18px", background: "#fff", color: "#4A5568", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      {shg && (
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

          {/* Left column — identity card */}
          <div style={{ width: "32%", flexShrink: 0 }}>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 16 }}>
              {/* Avatar area */}
              <div style={{ background: "linear-gradient(135deg,#1B5E20,#2E7D32)", padding: "28px 20px 24px", textAlign: "center" }}>
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", border: "3px solid rgba(255,255,255,0.35)" }}>
                  <i className="mdi mdi-account-group" style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{shg.name}</div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginBottom: 10 }}>{shg.vfcId?.vfcName || "—"}</div>
                {shg.code && (
                  <span style={{ display: "inline-block", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: "50rem", border: "1px solid rgba(255,255,255,0.3)" }}>
                    {shg.code}
                  </span>
                )}
              </div>

              {/* Key info rows */}
              <div style={{ padding: "12px 16px" }}>
                <InfoRow icon="mdi-tag-outline"           label="Type"                value={shg.type} />
                <InfoRow icon="mdi-map-marker-outline"    label="Location"            value={shg.location} />
                <InfoRow icon="mdi-calendar-outline"      label="Date of Establishment" value={shg.dateOfEstablishment ? new Date(shg.dateOfEstablishment).toLocaleDateString("en-IN") : null} />
                <InfoRow icon="mdi-home-outline"          label="Address"             value={shg.address} />
              </div>
            </div>
          </div>

          {/* Right column — detail cards */}
          <div style={{ flex: 1, minWidth: 0 }}>

            <SectionCard icon="mdi-map-marker-outline" title="Location Details">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <DetailCell label="Village"    value={shg.village} />
                <DetailCell label="Panchayath" value={shg.panchayath} />
                <DetailCell label="Thaluk"     value={shg.thaluk} />
                <DetailCell label="Pin Code"   value={shg.pinCode} />
                <DetailCell label="Address"    value={shg.address} />
                <DetailCell label="Location"   value={shg.location} />
              </div>
            </SectionCard>

            <SectionCard icon="mdi-bank-outline" title="Bank Details">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <DetailCell label="Bank Name"      value={shg.bankName} />
                <DetailCell label="Branch"         value={shg.branch} />
                <DetailCell label="Account Number" value={shg.accountNumber} />
                <DetailCell label="IFSC Code"      value={shg.ifscCode} />
              </div>
            </SectionCard>

            <SectionCard icon="mdi-information-outline" title="Basic Information">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <DetailCell label="SHG Name"           value={shg.name} />
                <DetailCell label="SHG Code"           value={shg.code} />
                <DetailCell label="SHG Type"           value={shg.type} />
                <DetailCell label="JFMC"               value={shg.vfcId?.vfcName} />
                <DetailCell label="Date of Establishment" value={shg.dateOfEstablishment ? new Date(shg.dateOfEstablishment).toLocaleDateString("en-IN") : null} />
              </div>
            </SectionCard>

          </div>
        </div>
      )}

      {/* Empty state when no SHG selected */}
      {!shg && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="mdi mdi-account-group-outline" style={{ fontSize: 30, color: "#2E7D32" }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No SHG Selected</div>
          <div style={{ fontSize: 13, color: "#A0AEC0" }}>Select a range, JFMC and SHG above to view the profile</div>
        </div>
      )}
    </div>
  );
};

export default SHGProfile;
